/**
 * <PassivePicker> — custom combobox for a single raid's passive skills.
 *
 * MOVED here verbatim from AdminRaids.jsx (2026-10-05) so the old
 * /admin/raids Quick Spawn card and the new /admin/raids/manual Summon panel
 * share ONE implementation. Two copies of this picker is the failure mode:
 * if both pages do not import this file, the extraction is not done.
 *
 * Empty selection = the spawner rolls 4 random passives from the chosen
 * category pool (the old behaviour). Picking any passive here overrides that
 * roll for THIS raid only — max 4, which is the in-game cap.
 * Options come from GET /admin/raids/passive-options, cached at module level
 * so the list is fetched once per session no matter how many cards mount.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAnchoredPanel } from "@/components/admin/useAnchoredPanel";

export const CATEGORY_EMOJI = { combat: "⚔️", speed: "💨", work: "⚒️" };

export const TIER_COLOR = {
  5: "text-yellow-200",
  4: "text-amber-300",
  3: "text-violet-300",
  2: "text-emerald-300",
  1: "text-sky-300",
};

let _passiveOptions = null;
let _passiveInflight = null;

/** The in-game rank arrow, same asset the Pal Builder badge uses. Falls back
    to a tiny tier pill when the backend has no icon for that rank. */
export function PassiveRankIcon({ tier, icon, size = 14 }) {
  if (icon) {
    const url = icon.startsWith("http") ? icon : `${process.env.REACT_APP_BACKEND_URL}${icon}`;
    return (
      <img
        src={url}
        alt={`Rank ${tier}`}
        title={`Rank ${tier}`}
        loading="lazy"
        style={{ width: size, height: size }}
        className="object-contain flex-shrink-0"
      />
    );
  }
  return (
    <span
      style={{ width: size, height: size }}
      className="inline-flex items-center justify-center rounded bg-white/5 text-[8px] text-[var(--muted)] flex-shrink-0"
      title={`Rank ${tier ?? "?"}`}
    >
      ·
    </span>
  );
}

/** Icon lookup by passive name — chips only carry the name. */
export function usePassiveIconMap() {
  const { all } = usePassiveOptions();
  return useMemo(() => {
    const map = {};
    for (const p of all || []) map[p.name] = p;
    return map;
  }, [all]);
}

export function usePassiveOptions() {
  const [opts, setOpts] = useState(_passiveOptions);
  useEffect(() => {
    if (_passiveOptions) return undefined;
    let cancelled = false;
    if (!_passiveInflight) {
      _passiveInflight = api.get("/admin/raids/passive-options")
        .then(({ data }) => { _passiveOptions = data; return data; })
        .catch(() => ({ categories: {}, all: [], max: 4 }))
        .finally(() => { _passiveInflight = null; });
    }
    _passiveInflight.then((d) => { if (!cancelled) setOpts(d); });
    return () => { cancelled = true; };
  }, []);
  return opts || { categories: {}, all: [], max: 4 };
}

export function PassivePicker({ selected, onChange, slug }) {
  const { categories, all, max = 4 } = usePassiveOptions();
  const byName = useMemo(() => {
    const map = {};
    for (const p of all || []) map[p.name] = p;
    return map;
  }, [all]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const toast = useToast();
  const close = useCallback(() => setOpen(false), []);
  // Portalled for the same reason as PalPicker: a .glass-card ancestor makes
  // its own stacking context, so an in-card panel loses to the next card.
  const { triggerRef, panelRef, style } = useAnchoredPanel(open, close);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q ? all.filter((p) => p.name.toLowerCase().includes(q)) : all;
    return rows.slice(0, 120);
  }, [all, query]);

  const toggle = (name) => {
    if (selected.includes(name)) {
      onChange(selected.filter((n) => n !== name));
      return;
    }
    if (selected.length >= max) {
      toast.info?.(`A pal can only hold ${max} passives — remove one first.`);
      return;
    }
    onChange([...selected, name]);
  };

  // A <select> gave keyboard nav for free; a custom listbox has to do it.
  const onKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      toggle(results[Math.min(cursor, results.length - 1)].name);
    }
  };

  const panel = (
    <div
      ref={panelRef}
      style={style || { display: "none" }}
      onKeyDown={onKeyDown}
      className="overflow-hidden rounded-md border border-white/10 bg-[#11131a] shadow-xl p-2 flex flex-col"
      data-testid={`admin-raids-passive-panel-${slug}`}
    >

          <div className="flex items-center gap-1.5 mb-1.5">
            <input
              autoFocus
              value={query}
              onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
              placeholder={`Search ${all.length} passives…`}
              className="input input-sm flex-1 text-[11px]"
              data-testid={`admin-raids-passive-search-${slug}`}
            />
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[10px] text-[var(--muted)] hover:text-[var(--bone)]"
                title="Back to automatic random passives"
                data-testid={`admin-raids-passive-clear-${slug}`}
              >
                Auto
              </button>
            )}
          </div>

          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1.5">
              {selected.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => toggle(n)}
                  className="text-[10px] px-1.5 py-0.5 rounded-full border border-violet-400/40 bg-violet-500/10 text-violet-200 flex items-center gap-1"
                  data-testid={`admin-raids-passive-chip-${slug}-${n.replace(/\s+/g, "-")}`}
                >
                  <PassiveRankIcon tier={byName[n]?.tier} icon={byName[n]?.icon} size={12} />
                  <span className={TIER_COLOR[byName[n]?.tier] || "text-violet-200"}>{n}</span>
                  <X size={9} />
                </button>
              ))}
            </div>
          )}

          {!query && Object.keys(categories).length > 0 && (
            <div className="mb-1.5 space-y-1">
              <div className="text-[9px] uppercase tracking-wider text-[var(--muted)]">
                Quick fill from a raid pool
              </div>
              <div className="flex gap-1 flex-wrap">
                {Object.entries(categories).map(([cat, names]) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => onChange(names.slice(0, max))}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 hover:bg-white/5"
                    title={names.join(", ")}
                    data-testid={`admin-raids-passive-cat-${slug}-${cat}`}
                  >
                    {CATEGORY_EMOJI[cat] || ""} {cat}
                  </button>
                ))}
              </div>
            </div>
          )}

          <ul className="overflow-y-auto space-y-0.5 flex-1 min-h-0">
            {results.map((p, i) => (
              <li key={p.name}>
                <button
                  type="button"
                  onClick={() => toggle(p.name)}
                  onMouseEnter={() => setCursor(i)}
                  className={`w-full text-left flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/5 ${
                    i === cursor ? "bg-white/5" : ""
                  }`}
                  data-testid={`admin-raids-passive-option-${slug}-${p.name.replace(/\s+/g, "-")}`}
                >
                  <PassiveRankIcon tier={p.tier} icon={p.icon} />
                  <span className={`text-[11px] flex-1 truncate ${TIER_COLOR[p.tier] || "text-[var(--bone)]"}`}>
                    {p.name}
                  </span>
                  <span className="text-[9px] text-[var(--muted)]">T{p.tier}</span>
                  {selected.includes(p.name) && <Check size={11} className="text-[var(--teal)]" />}
                </button>
              </li>
            ))}
            {results.length === 0 && (
              <li className="text-[11px] text-[var(--muted)] px-1.5 py-2">No match.</li>
            )}
          </ul>
    </div>
  );

  return (
    <div className="relative" onKeyDown={onKeyDown}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => { setOpen((o) => !o); setQuery(""); setCursor(0); }}
        className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
          selected.length
            ? "border-violet-400/40 bg-violet-500/10 text-violet-200"
            : "border-white/10 bg-white/[0.03] text-[var(--muted)]"
        }`}
        title="Hand-pick this raid's passives. Leave empty to roll 4 random ones from the category pool."
        data-testid={`admin-raids-passives-${slug}`}
      >
        🧬 {selected.length ? `${selected.length}/${max} passives` : "Auto passives"}
      </button>

      {open && createPortal(panel, document.body)}
    </div>
  );
}
